import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { templateContainer, tokens, content, column, chunk, pageContent, pageSelectors, page, paddingTop, paddingBottom, footer } from './comp.page.style.js';
import { Slot } from './comp.slot.js';
import { calculateDimensions } from './util.calculate-physical-page-dimensions.js';
import { waitForContent } from './util.wait-for-content.js';
import { jsxs, jsx } from 'react/jsx-runtime';

/**
 * Constitutes a single PrintPage, _however_ such a page may span multiple
 * physical pages depending on its content.
 */
function Page(props) {
  const ref = useRef(null);
  const templatePageRef = useRef(null);
  const [dimensions, setDimensions] = useState();
  useEffect(() => {
    const templatePage = templatePageRef.current;
    if (!templatePage) {
      return;
    }
    waitForContent(templatePage).then(() => {
      const dimensions = calculateDimensions({
        target: props.target,
        templatePage,
        padding: props.page.padding,
        currentContext: props.context,
        slots: props.config.slots
      });
      setDimensions(dimensions);
      props.onPhysicalPagesRendered(props.context.virtual.currentPageIndexInSection, dimensions.pages.length);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    children: [!!dimensions && !!props.context.physical && dimensions.pages.map((it, index) => /*#__PURE__*/jsx(PhysicalPage, {
      ...props,
      pageDimensions: it,
      chunkDimensions: dimensions.chunks,
      physicalPageIndex: index
    }, index)), /*#__PURE__*/jsx("div", {
      className: templateContainer,
      "data-template": true,
      children: /*#__PURE__*/jsx("div", {
        ref: templatePageRef,
        className: cx(page, props.page.className),
        children: /*#__PURE__*/jsx("div", {
          className: cx(pageContent, pageSelectors.chunkContainer.className),
          children: props.page.chunks.map((chunk$1, chunkIndex) => /*#__PURE__*/jsx("div", {
            className: cx(chunk, chunk$1.containerClassName),
            style: tokens.columnGap.__override("columns" in chunk$1 ? chunk$1.columnGap : undefined),
            children: ("columns" in chunk$1 ? chunk$1.columns : [chunk$1]).map((column$1, columnIndex) => /*#__PURE__*/jsx("div", {
              className: cx(column, column$1.columnClassName),
              style: column$1.styles,
              children: /*#__PURE__*/jsx("div", {
                className: content,
                children: column$1.content
              })
            }, columnIndex))
          }, chunkIndex))
        })
      })
    })]
  });
}
function PhysicalPage(props) {
  const context = {
    virtual: props.context.virtual,
    physical: props.context.physical ? {
      ...props.context.physical,
      currentPageIndexInSection: props.context.physical.currentPageIndexInSection + props.physicalPageIndex,
      currentPageIndexInDocument: props.context.physical.currentPageIndexInDocument + props.physicalPageIndex
    } : undefined
  };
  const padding = props.page.padding?.(context);
  return /*#__PURE__*/jsxs("div", {
    className: cx(page, props.page.className),
    style: tokens.__override({
      paddingTop: padding?.top,
      paddingBottom: padding?.bottom
    }),
    children: [!!props.config.slots?.header && /*#__PURE__*/jsx("div", {
      className: pageSelectors.header.className,
      children: /*#__PURE__*/jsx(Slot, {
        slot: props.config.slots.header,
        context: context
      })
    }), /*#__PURE__*/jsx("div", {
      className: paddingTop
    }), /*#__PURE__*/jsx("div", {
      className: pageContent,
      style: {
        height: props.pageDimensions.heightInPx
      },
      children: /*#__PURE__*/jsx("div", {
        className: pageSelectors.chunkContainer.className,
        style: {
          transform: `translateY(${props.pageDimensions.translateYInPx}px)`
        },
        children: props.page.chunks.map((chunk$1, chunkIndex) => {
          const dimensions = props.chunkDimensions.get(chunkIndex);
          const dimensionsOnPage = dimensions?.onPage[props.physicalPageIndex];
          const columns = "columns" in chunk$1 ? chunk$1.columns : [chunk$1];
          return /*#__PURE__*/jsx("div", {
            className: cx(chunk, chunk$1.containerClassName),
            style: {
              ...tokens.columnGap.__override("columns" in chunk$1 ? chunk$1.columnGap : undefined),
              height: dimensions?.chunkHeightInPx
            },
            children: columns.map((column$1, columnIndex) => /*#__PURE__*/jsx("div", {
              className: cx(column, column$1.columnClassName),
              style: {
                ...column$1.styles,
                height: dimensionsOnPage?.columns[columnIndex]?.heightInPx,
                transform: dimensionsOnPage?.columns[columnIndex]?.translateYInPx ? `translateY(${dimensionsOnPage.columns[columnIndex].translateYInPx}px)` : undefined
              },
              children: /*#__PURE__*/jsx("div", {
                className: content,
                children: column$1.content
              })
            }, columnIndex))
          }, chunkIndex);
        })
      })
    }), /*#__PURE__*/jsx("div", {
      className: paddingBottom
    }), !!props.config.slots?.footer && /*#__PURE__*/jsx("div", {
      className: footer,
      children: /*#__PURE__*/jsx(Slot, {
        context: context,
        slot: props.config.slots?.footer
      })
    })]
  });
}

export { Page };
//# sourceMappingURL=comp.page.js.map
